/**
 * Enhanced request deduplication and circuit breaker utilities
 * Prevents infinite loops and manages failed requests gracefully
 */

// API Base URL configuration
const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5000/api'  // Local development
  : process.env.REACT_APP_API_BASE_URL || 'https://sparvi-webapp-fjdjdvh2bse9d0gm.centralus-01.azurewebsites.net/api';

// Store for tracking ongoing requests with timestamps
const ongoingRequests = new Map();

// Circuit breaker state for different endpoints
const circuitBreakers = new Map();

// Store for abort controllers by request ID
const abortControllers = new Map();

// Request frequency tracking
const requestFrequency = new Map();
const REQUEST_THROTTLE_WINDOW = 5000; // 5 seconds
const MAX_REQUESTS_PER_WINDOW = 3;

// Configuration
const CIRCUIT_BREAKER_CONFIG = {
  failureThreshold: 5,        // Number of failures before opening circuit
  resetTimeoutMs: 30000,      // 30 seconds before attempting reset
  monitorWindowMs: 60000,     // 1 minute window for tracking failures
};

const RETRY_CONFIG = {
  // 1 retry (= 2 attempts max) is enough to cover cold-start races without
  // creating a request storm. Previously 3, which combined with a 10s timeout
  // and a single-threaded dev backend produced ~50s waits per endpoint when
  // the Supabase HTTP/2 handshake was cold.
  maxRetries: 1,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
};

/**
 * Enhanced Circuit breaker implementation
 */
class CircuitBreaker {
  constructor(key, config = CIRCUIT_BREAKER_CONFIG) {
    this.key = key;
    this.config = config;
    this.state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN
    this.failures = [];
    this.lastFailureTime = null;
    this.nextAttemptTime = null;
  }

  canExecute() {
    const now = Date.now();

    // Clean old failures outside the monitoring window
    this.failures = this.failures.filter(
      time => now - time < this.config.monitorWindowMs
    );

    switch (this.state) {
      case 'CLOSED':
        return true;

      case 'OPEN':
        if (now >= this.nextAttemptTime) {
          this.state = 'HALF_OPEN';
          console.log(`Circuit breaker HALF_OPEN for ${this.key}`);
          return true;
        }
        return false;

      case 'HALF_OPEN':
        return true;

      default:
        return false;
    }
  }

  recordSuccess() {
    this.failures = [];
    this.state = 'CLOSED';
    this.nextAttemptTime = null;
    console.log(`Circuit breaker CLOSED for ${this.key}`);
  }

  recordFailure() {
    const now = Date.now();
    this.failures.push(now);
    this.lastFailureTime = now;

    if (this.failures.length >= this.config.failureThreshold) {
      this.state = 'OPEN';
      this.nextAttemptTime = now + this.config.resetTimeoutMs;
      console.warn(`Circuit breaker OPENED for ${this.key}. Next attempt at ${new Date(this.nextAttemptTime)}`);
    }
  }

  getState() {
    return {
      state: this.state,
      failures: this.failures.length,
      nextAttemptTime: this.nextAttemptTime,
      canExecute: this.canExecute()
    };
  }
}

/**
 * Get or create circuit breaker for endpoint
 */
function getCircuitBreaker(key) {
  if (!circuitBreakers.has(key)) {
    circuitBreakers.set(key, new CircuitBreaker(key));
  }
  return circuitBreakers.get(key);
}

/**
 * Enhanced request key generation with better uniqueness
 */
function getRequestKey(url, method = 'GET', params = {}, requestId = null) {
  const paramString = Object.keys(params).length > 0
    ? `?${new URLSearchParams(params).toString()}`
    : '';

  const baseKey = `${method}:${url}${paramString}`;

  // If requestId is provided, use it for uniqueness
  if (requestId) {
    return `${baseKey}#${requestId}`;
  }

  return baseKey;
}

/**
 * Check request frequency to prevent spam
 */
function checkRequestFrequency(key) {
  const now = Date.now();

  if (!requestFrequency.has(key)) {
    requestFrequency.set(key, []);
  }

  const requests = requestFrequency.get(key);

  // Clean old requests outside the window
  const recentRequests = requests.filter(time => now - time < REQUEST_THROTTLE_WINDOW);
  requestFrequency.set(key, recentRequests);

  // Check if we're over the limit
  if (recentRequests.length >= MAX_REQUESTS_PER_WINDOW) {
    console.warn(`Request frequency limit exceeded for ${key}. ${recentRequests.length} requests in last ${REQUEST_THROTTLE_WINDOW}ms`);
    return false;
  }

  // Record this request
  recentRequests.push(now);
  requestFrequency.set(key, recentRequests);

  return true;
}

/**
 * Sleep utility for delays
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Calculate exponential backoff delay
 */
function calculateBackoffDelay(attempt, baseDelay = RETRY_CONFIG.baseDelayMs, maxDelay = RETRY_CONFIG.maxDelayMs) {
  const delay = baseDelay * Math.pow(2, attempt - 1);
  const jitter = Math.random() * 0.1 * delay; // Add 10% jitter
  return Math.min(delay + jitter, maxDelay);
}

/**
 * Enhanced fetch with circuit breaker, deduplication, and retry logic
 */
export async function safeFetch(url, options = {}) {
  const {
    method = 'GET',
    params = {},
    retries = RETRY_CONFIG.maxRetries,
    // 30s default covers cold-start backends (Azure container warmup, cold
    // Supabase HTTP/2 handshake) in a single attempt. Previously 10s, which
    // forced a retry on every cold request and triggered cascading storms
    // against single-threaded dev backends.
    timeout = 30000,
    requestId = null,
    skipDeduplication = false,
    skipFrequencyCheck = false,
    ...fetchOptions
  } = options;

  // Generate unique key for this request
  const requestKey = getRequestKey(url, method, params, requestId);
  const frequencyKey = getRequestKey(url, method, params); // Without requestId for frequency check

  // Check request frequency (unless skipped)
  if (!skipFrequencyCheck && !checkRequestFrequency(frequencyKey)) {
    const error = new Error(`Request frequency limit exceeded for ${url}`);
    error.frequencyLimited = true;
    throw error;
  }

  // Check circuit breaker
  const circuitBreaker = getCircuitBreaker(url);
  if (!circuitBreaker.canExecute()) {
    const nextAttemptTime = circuitBreaker.nextAttemptTime;
    const waitTime = nextAttemptTime ? Math.ceil((nextAttemptTime - Date.now()) / 1000) : 0;
    
    const error = new Error(
      `Circuit breaker is OPEN for ${url}. ` +
      `${waitTime > 0 ? `Retry in ${waitTime} seconds.` : 'Retrying soon.'}`
    );
    error.circuitBreakerOpen = true;
    error.nextAttemptTime = nextAttemptTime;
    error.waitTimeSeconds = waitTime;
    error.endpoint = url;
    
    console.warn(`Circuit breaker blocked request to ${url}. Wait ${waitTime}s before retry.`);
    throw error;
  }

  // Check for ongoing identical request (unless skipped)
  if (!skipDeduplication && ongoingRequests.has(requestKey)) {
    console.log(`Deduplicating request: ${requestKey}`);
    return ongoingRequests.get(requestKey);
  }

  // Create the actual request promise
  const requestPromise = executeRequestWithRetry(url, {
    method,
    params,
    retries,
    timeout,
    circuitBreaker,
    requestId,
    ...fetchOptions
  });

  // Store the promise for deduplication (unless skipped)
  if (!skipDeduplication) {
    ongoingRequests.set(requestKey, requestPromise);
  }

  try {
    const result = await requestPromise;
    circuitBreaker.recordSuccess();
    return result;
  } catch (error) {
    // Don't record failure for cancelled or frequency limited requests
    if (!error.cancelled && !error.frequencyLimited) {
      circuitBreaker.recordFailure();
    }
    throw error;
  } finally {
    // Clean up the ongoing request
    if (!skipDeduplication) {
      ongoingRequests.delete(requestKey);
    }
  }
}

/**
 * Execute request with retry logic
 */
async function executeRequestWithRetry(url, options) {
  const { method, params, retries, timeout, circuitBreaker, requestId, ...fetchOptions } = options;

  // Build full URL with API base URL and params
  let fullUrl = url;
  
  // Add API base URL if this is a relative path
  if (url.startsWith('/')) {
    fullUrl = `${API_BASE_URL}${url}`;
  }
  
  // Add query parameters if any
  if (Object.keys(params).length > 0) {
    const separator = fullUrl.includes('?') ? '&' : '?';
    fullUrl = `${fullUrl}${separator}${new URLSearchParams(params).toString()}`;
  }

  let lastError;

  for (let attempt = 1; attempt <= retries + 1; attempt++) {
    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      console.log(`Making request (attempt ${attempt}/${retries + 1}): ${method} ${fullUrl}${requestId ? ` [${requestId}]` : ''}`);

      const response = await fetch(fullUrl, {
        method,
        signal: controller.signal,
        ...fetchOptions
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText };
        }

        const error = new Error(errorData.error || `HTTP ${response.status}`);
        error.status = response.status;
        error.response = response;
        error.data = errorData;
        throw error;
      }

      const data = await response.json();
      console.log(`Request successful: ${method} ${fullUrl}${requestId ? ` [${requestId}]` : ''}`);
      return data;

    } catch (error) {
      lastError = error;

      // Convert timeout AbortError into a retryable error. The previous
      // behavior threw immediately with cancelled=true, which short-circuited
      // the retry loop AND prevented the circuit breaker from recording the
      // failure. Cold-start backends frequently exceed the 10s default; a
      // single retry typically succeeds once the connection pool is warm.
      if (error.name === 'AbortError') {
        const timeoutError = new Error('Request timeout');
        timeoutError.timeout = true;
        lastError = timeoutError;
      }

      // Don't retry on auth errors (401, 403) or client errors (400-499)
      if (error.status && error.status >= 400 && error.status < 500) {
        console.warn(`Non-retryable error (${error.status}): ${error.message}`);
        break;
      }

      // Don't retry if circuit breaker is open
      if (error.circuitBreakerOpen) {
        console.warn('Circuit breaker open, not retrying');
        break;
      }

      // If we have more attempts, wait before retrying
      if (attempt <= retries) {
        const delay = calculateBackoffDelay(attempt);
        console.warn(`Request failed (attempt ${attempt}/${retries + 1}), retrying in ${delay}ms: ${error.message}`);
        await sleep(delay);
      }
    }
  }

  throw lastError;
}

/**
 * Get or create abort controller for a request
 * @param {string} requestId - Unique identifier for the request
 * @returns {AbortController} Abort controller for the request
 */
export function getRequestAbortController(requestId) {
  if (!abortControllers.has(requestId)) {
    abortControllers.set(requestId, new AbortController());
  }
  return abortControllers.get(requestId);
}

/**
 * Mark a request as completed and clean up its abort controller
 * @param {string} requestId - Unique identifier for the request
 */
export function requestCompleted(requestId) {
  if (abortControllers.has(requestId)) {
    abortControllers.delete(requestId);
  }
}

/**
 * Debounce function to limit the rate of function calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @param {boolean} immediate - Whether to execute on leading edge
 * @returns {Function} Debounced function
 */
export function debounce(func, wait, immediate = false) {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func.apply(this, args);
    };

    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);

    if (callNow) func.apply(this, args);
  };
}

/**
 * Enhanced batch requests with better error handling
 * @param {Array} requests - Array of request objects
 * @param {Object} options - Request options
 * @returns {Promise} Promise resolving to batch results
 */
export async function batchRequests(requests, options = {}) {
  const {
    timeout = 30000,
    signal = null
  } = options;

  // Filter out empty or invalid requests
  const validRequests = requests.filter(req => req && req.path);

  if (validRequests.length === 0) {
    console.warn('No valid requests to batch');
    return {};
  }

  try {
    console.log(`Making batch request with ${validRequests.length} requests`);
    console.log('Batch request details:', {
      requestCount: validRequests.length,
      requests: validRequests.map(req => ({ id: req.id, path: req.path, method: req.method })),
      timeout,
      timestamp: new Date().toISOString()
    });

    const startTime = Date.now();

    // Use safeFetch for the batch request with proper deduplication
    const response = await safeFetch('/batch', {
      method: 'POST',
      body: JSON.stringify({ requests: validRequests }),
      timeout,
      requestId: `batch-${Date.now()}`,
      skipDeduplication: false, // Allow deduplication for batch requests
      skipFrequencyCheck: true, // Skip frequency check for batch requests
      headers: {
        'Content-Type': 'application/json'
      },
      signal
    });

    const results = response.results || {};
    const responseTime = Date.now() - startTime;
    
    updateBatchRequestStats(true, responseTime, false);
    
    console.log('Batch request completed successfully:', {
      requestCount: validRequests.length,
      resultCount: Object.keys(results).length,
      resultKeys: Object.keys(results),
      responseTime: responseTime + 'ms',
      timestamp: new Date().toISOString()
    });
    return results;
  } catch (error) {
    console.error('Batch request failed:', error);

    // Handle circuit breaker errors specifically
    if (error.circuitBreakerOpen) {
      console.warn('Batch request blocked by circuit breaker:', error.message);
      
      updateBatchRequestStats(false, null, true);
      
      // Add circuit breaker specific error properties
      error.isBatchCircuitBreakerError = true;
      error.batchRequestCount = validRequests.length;
      
      throw error;
    }

    // Return partial results if available
    if (error.response && error.data && error.data.results) {
      console.warn('Returning partial batch results due to error');
      return error.data.results;
    }

    // Track failed batch request
    updateBatchRequestStats(false, null, false);
    
    // Add additional context for batch-specific errors
    error.isBatchRequestError = true;
    error.batchRequestCount = validRequests.length;
    throw error;
  }
}

/**
 * Get circuit breaker status for monitoring
 */
export function getCircuitBreakerStatus() {
  const status = {};
  for (const [key, breaker] of circuitBreakers.entries()) {
    status[key] = breaker.getState();
  }
  return status;
}

/**
 * Reset circuit breaker (for manual recovery)
 */
export function resetCircuitBreaker(key) {
  if (circuitBreakers.has(key)) {
    circuitBreakers.delete(key);
    console.log(`Circuit breaker reset for ${key}`);
  }
}

/**
 * Reset all circuit breakers
 */
export function resetAllCircuitBreakers() {
  circuitBreakers.clear();
  ongoingRequests.clear();
  requestFrequency.clear();
  console.log('All circuit breakers and request tracking reset');
}

/**
 * Force reset batch API circuit breaker for emergency recovery
 */
export function resetBatchCircuitBreaker() {
  const batchUrls = ['/batch', '/api/batch'];
  let resetCount = 0;
  
  for (const url of batchUrls) {
    if (circuitBreakers.has(url)) {
      circuitBreakers.delete(url);
      resetCount++;
      console.log(`Emergency reset of batch circuit breaker for ${url}`);
    }
  }
  
  // Also clear any ongoing batch requests
  const batchRequestKeys = Array.from(ongoingRequests.keys()).filter(key => 
    key.includes('batch') || key.includes('/batch')
  );
  
  batchRequestKeys.forEach(key => {
    ongoingRequests.delete(key);
    console.log(`Cleared ongoing batch request: ${key}`);
  });
  
  return resetCount > 0;
}

/**
 * Get ongoing requests for monitoring
 */
export function getOngoingRequests() {
  return Array.from(ongoingRequests.keys());
}

/**
 * Get request frequency stats for monitoring
 */
export function getRequestFrequencyStats() {
  const stats = {};
  for (const [key, requests] of requestFrequency.entries()) {
    stats[key] = {
      count: requests.length,
      lastRequest: requests.length > 0 ? new Date(Math.max(...requests)) : null
    };
  }
  return stats;
}

/**
 * Cancel all ongoing requests (useful for cleanup)
 */
export function cancelAllRequests() {
  console.log('Cancelling all ongoing requests');
  ongoingRequests.clear();

  // Also abort all ongoing controllers
  for (const controller of abortControllers.values()) {
    try {
      controller.abort();
    } catch (error) {
      console.warn('Error aborting controller:', error);
    }
  }
  abortControllers.clear();
}

/**
 * Clear old tracking data (useful for cleanup)
 */
export function cleanupOldTracking() {
  const now = Date.now();

  // Clean up old request frequency data
  for (const [key, requests] of requestFrequency.entries()) {
    const recentRequests = requests.filter(time => now - time < REQUEST_THROTTLE_WINDOW * 2);
    if (recentRequests.length === 0) {
      requestFrequency.delete(key);
    } else {
      requestFrequency.set(key, recentRequests);
    }
  }
}

// Periodic cleanup
setInterval(cleanupOldTracking, 60000); // Every minute

// Batch request statistics tracking
const batchRequestStats = {
  totalRequests: 0,
  successfulRequests: 0,
  failedRequests: 0,
  circuitBreakerBlocks: 0,
  lastRequestTime: null,
  averageResponseTime: 0,
  responseTimes: []
};

/**
 * Update batch request statistics
 */
function updateBatchRequestStats(success, responseTime = null, wasCircuitBreakerBlocked = false) {
  batchRequestStats.totalRequests++;
  batchRequestStats.lastRequestTime = new Date().toISOString();
  
  if (wasCircuitBreakerBlocked) {
    batchRequestStats.circuitBreakerBlocks++;
  } else if (success) {
    batchRequestStats.successfulRequests++;
    if (responseTime) {
      batchRequestStats.responseTimes.push(responseTime);
      // Keep only last 50 response times
      if (batchRequestStats.responseTimes.length > 50) {
        batchRequestStats.responseTimes = batchRequestStats.responseTimes.slice(-50);
      }
      // Update average
      batchRequestStats.averageResponseTime = 
        batchRequestStats.responseTimes.reduce((a, b) => a + b, 0) / batchRequestStats.responseTimes.length;
    }
  } else {
    batchRequestStats.failedRequests++;
  }
}

/**
 * Get batch request statistics for debugging
 */
export function getBatchRequestStats() {
  return {
    ...batchRequestStats,
    successRate: batchRequestStats.totalRequests > 0 
      ? (batchRequestStats.successfulRequests / batchRequestStats.totalRequests * 100).toFixed(1) + '%'
      : '0%'
  };
}

/**
 * Reset batch request statistics
 */
export function resetBatchRequestStats() {
  batchRequestStats.totalRequests = 0;
  batchRequestStats.successfulRequests = 0;
  batchRequestStats.failedRequests = 0;
  batchRequestStats.circuitBreakerBlocks = 0;
  batchRequestStats.lastRequestTime = null;
  batchRequestStats.averageResponseTime = 0;
  batchRequestStats.responseTimes = [];
  console.log('Batch request statistics reset');
}